// src/utils/expiry.js
import dayjs from 'dayjs'

/**
 * Returns true if a bet should be treated as expired.
 *
 * Logic:
 * 1) Any status !== 'open' → expired.
 * 2) Live markets (val.live === 'l' or val.is_live === true) → expire only when val.stale === true.
 * 3) Pre-match markets (val.live === 'p' or missing) → expire once kickoff + graceMinutes is before now.
 */
export function isFixtureExpired(bet, { graceMinutes = 0 } = {}) {
  if (!bet) return false

  const val = bet._custom?.value || bet

  let statusName = ''
  let dateStr = ''

  // Check if bet details are directly on the bet object
  if (val.status_name && val.date) {
    statusName = val.status_name.toLowerCase().trim()
    dateStr = val.date
  }
  // Handle nested data structures
  else if (bet.status_name && bet.date) {
    statusName = bet.status_name.toLowerCase().trim()
    dateStr = bet.date
  }

  // 1) non‐open markets always expired
  if (statusName && statusName !== 'open') {
    return true
  }

  // 2) only treat `val.live === 'l'` as live
  const isLive = val.live === 'l'
  if (isLive) {
    // expire only when WebSocket explicitly flagged stale
    return !!val.stale
  }

  // 3) pre‐match → expire when (kickoff + grace) < now
  if (dateStr) {
    const m = dayjs(dateStr, 'YYYY-MM-DD HH:mm:ss')
    if (!m.isValid() || m.add(graceMinutes, 'minute').isBefore(dayjs())) {
      return true
    }
  }

  return false
}

/**
 * Format date string for display
 * @param {string} dateString - Date string in format 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} - Formatted date string
 */
export function formatDate(dateString) {
  if (!dateString) return ''
  const date = dayjs(dateString, 'YYYY-MM-DD HH:mm:ss')
  if (!date.isValid()) return ''
  return date.format('DD/MM, hh:mm A')
}
